import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Briefcase, FolderKanban, Hourglass, ListChecks, Timer, Users } from 'lucide-react';
import { PageLayout } from '../../../../components/ui/PageLayout';
import Select from '../../../../components/ui/Select';
import {
  EMPTY_VALUE,
  ExportMenu,
  PeriodPicker,
  ReportEmpty,
  ReportError,
  ReportFilterBar,
  ReportFilterField,
  ReportIdle,
  ReportLoading,
  ReportSummaryStrip,
  ReportTable,
  RunButton,
  ScopeFilter,
  StatusChip,
  buildExportRequest,
  formatDayShort,
  formatHours,
  formatInt,
  formatPeriod,
  parseHours,
  reportExportFilename,
  reportExportLabels,
  resolvePeriodPreset,
  useGroupCollapse,
  useReportExport,
  useScopeFilter,
} from '../../../../components/reports';
import type {
  PeriodValue,
  ReportColumn,
  ReportExportFormat,
  ReportGroup,
  ReportSummaryItem,
} from '../../../../components/reports';
import { postReportExport } from '../../../../api/reports';
import type {
  WorkSessionReportView,
  WorkSessionsReportInput,
} from '../../../../models/projectManagement';
import * as ReportsApi from '../../../../api/Attendance/reports';
import { getProjects } from '../../../../api/ProjectManagement/project';
import { getApiErrorMessage } from '../../../../utils/apiErrors';

/**
 * Attendance › Work Sessions, on the shared reports shell.
 *
 * 🔴 WHAT THIS REPLACED — AND THE PAGER THAT WAS PURE THEATRE.
 *
 * The old tab (329 lines around `components/reportsController`) rendered a flat table under a
 * `Pagination` control that could not work, for three compounding reasons:
 *
 *  1. It sent `offset` and `limit: 10` **on a contract that has no such fields.**
 *     `WorkSessionsReportInput` is `userId, userIds, departmentIds, countryIds, branchIds,
 *     projectIds, searchMethod, from, to` — nothing else — and the two extra keys compiled only
 *     because `input` was an unannotated object literal. `GetWorkSessionsPerUserAsync` has no
 *     `Skip`/`Take`; the server always returned the WHOLE result set.
 *  2. `setCount(payload.length)` therefore recorded the size of the whole set, so `totalPages`
 *     was `ceil(all / 10)` — a plausible-looking number arrived at by accident.
 *  3. The table rendered `items.map(...)` **unsliced**. Every page showed every row, and clicking
 *     page 2 re-fired the fetch with an `offset` the server ignored, producing identical output.
 *
 * So the control offered navigation through a list that never moved. It is gone: rows are grouped
 * per employee on `ReportTable`, which is the shape a reader of this report actually wants — "how
 * much time did each person log, and against what" — and no server paging was added, because that
 * is a contract change and this report's volumes are per-period.
 *
 * Two further defects went with it: the tab ran on mount over a "last 7 days → tomorrow" window
 * (nothing runs here until Run is pressed), and it concatenated both task comments into ONE cell
 * with the labels baked into the value (`Start Task : x | End Task : y`), which no export could
 * address as data. They are two columns now.
 *
 * 🔴 THE CROSS-TENANT READ THIS LANE FIXED. `GetWorkSessionsPerUserAsync` used to start from
 * `context.Users.AsQueryable()` — no `CompanyId` filter, no `GetAuthorizedUserIdsAsync`
 * intersection — and apply `input.userIds` verbatim, so a caller naming another tenant's user ids
 * read that tenant's work sessions. The service now scopes to the CALLER's company and authority
 * before any client filter, and the controller hands it the caller rather than whoever `userId`
 * named. See the security note on `ReportsService.GetWorkSessionsPerUserAsync`.
 */

/**
 * A wall-clock reading from the wire.
 *
 * ⚠️ READ TEXTUALLY. `startTime` / `endTime` are the `WorkSession.StartTime` / `EndTime` columns —
 * LOCAL wall-clock values stamped in the EMPLOYEE's timezone and serialised with no offset (the
 * instants live in the separate `*TimeUtc` columns, which this view model does not carry). Handing
 * them to `new Date` makes the browser re-interpret them in the READER's timezone, which is a no-op
 * only when reader and employee happen to share one. Same rule, same reason, as `Shifts.tsx` and
 * `Clockings.tsx`. (This is the OPPOSITE of `OutOfBounds.tsx`, whose `timeUtc` is a genuine
 * instant — the two reports are not interchangeable on this point.)
 */
function wallClock(value: string | Date | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /T(\d{2}):(\d{2})/.exec(value);
    if (match) return `${match[1]}:${match[2]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** The LOCAL calendar day of a value, `yyyy-mm-dd`. Read textually for the same reason as above. */
function dayKeyOf(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Below this, a duration is not a figure — 18 seconds, which `formatHours` renders as `0h`. */
const HOUR_EPSILON = 0.005;

/**
 * The session's length in decimal hours.
 *
 * ⚠️ AN OPEN SESSION IS NOT ZERO. `WorkSession.Duration` is a computed property:
 * `EndTimeUtc != null ? EndTimeUtc - StartTimeUtc : DateTime.UtcNow - StartTimeUtc`. So a session
 * somebody started and never stopped reports its ELAPSED-SO-FAR, a figure that grows while the page
 * is open. It is counted here (and by the export, from the same property, so the two agree), and the
 * row is marked open so a reader knows which of their hours are still running.
 */
function hoursOf(row: WorkSessionReportView): number {
  return parseHours(row.duration) ?? 0;
}

const isOpen = (row: WorkSessionReportView) => !row.endTime;

interface WorkSessionsTotals {
  sessions: number;
  hours: number;
  open: number;
  projectIds: Set<number>;
}

function emptyTotals(): WorkSessionsTotals {
  return { sessions: 0, hours: 0, open: 0, projectIds: new Set<number>() };
}

function addRow(totals: WorkSessionsTotals, row: WorkSessionReportView): WorkSessionsTotals {
  if (row.projectId != null) totals.projectIds.add(row.projectId);
  return {
    sessions: totals.sessions + 1,
    hours: totals.hours + hoursOf(row),
    open: totals.open + (isOpen(row) ? 1 : 0),
    projectIds: totals.projectIds,
  };
}

interface WorkSessionsGroup extends ReportGroup<WorkSessionReportView> {
  employeeId: string;
  totals: WorkSessionsTotals;
}

interface ProjectOption {
  id: number;
  title: string;
}

export default function WorkSessions() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { userId, companyId } = useSelector(
    (state: {
      user?: { userId?: string; companyId?: number; company?: { id?: number } };
    }) => ({
      userId: state.user?.userId,
      companyId: state.user?.companyId ?? state.user?.company?.id,
    }),
  );

  /** DAY mode, defaulting to THIS MONTH — replacing the old "last 7 days → tomorrow" window. */
  const [period, setPeriod] = useState<PeriodValue>(
    () => resolvePeriodPreset('thisMonth', 'day') ?? { from: '', to: '' },
  );

  const scope = useScopeFilter({ companyId, userId });

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectIds, setProjectIds] = useState<number[]>([]);

  const [loading, setLoading] = useState(false);
  // "Nobody logged time in this range" and "nobody has asked yet" are different screens;
  // `rows.length` cannot tell them apart, so the fact that a report was RUN is held explicitly.
  const [hasRun, setHasRun] = useState(false);
  const [rows, setRows] = useState<WorkSessionReportView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const { busy: exporting, exportReport } = useReportExport();

  const invalidRange = !!period.from && !!period.to && period.from > period.to;

  // The project picker is a FILTER, not a prerequisite: a company with no projects still has work
  // sessions (they are simply unassigned), so a failed read costs the report nothing.
  useEffect(() => {
    let cancelled = false;
    getProjects()
      .then((res) => {
        if (cancelled) return;
        setProjects(
          (res?.payload ?? [])
            .filter((p) => p?.id != null && !!p.title)
            .map((p) => ({ id: p.id, title: p.title })),
        );
      })
      .catch(() => {
        /* no projects picker; the rest of the report is unaffected */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── the request ──────────────────────────────────────────────────────────────────────────── */

  /**
   * 🔴 AN EMPTY `userIds` DOES NOT MEAN "EVERYONE" ON THIS ENDPOINT, and an EMPTY ARRAY does not
   * mean the same thing as a MISSING one — this service has two traps where its siblings have one.
   *
   * `GetWorkSessionsPerUserAsync` collapses a request with `userIds == null && departmentIds == null
   * && countryIds == null && branchIds == null` to **the caller alone**. But the test is `== null`,
   * so an empty ARRAY skips that branch and falls through to the `else`, which with all-null filters
   * matches everyone the caller is allowed to see. Two adjacent shapes, two opposite answers.
   *
   * Naming the roster explicitly side-steps both, which is the Shifts recipe. It cannot widen
   * anything: since this lane's fix the service constrains to the caller's `CompanyId` and
   * intersects with `GetAuthorizedUserIdsAsync` BEFORE any of this is applied.
   */
  const buildInput = useCallback((): WorkSessionsReportInput => {
    const scoped = scope.resolvedUserIds.length > 0 ? scope.resolvedUserIds : scope.roster.map((p) => p.id);
    return {
      userId: userId ?? '',
      // LOCAL calendar dates: `PeriodPicker` never produces a UTC instant and neither may this.
      from: new Date(`${period.from}T00:00:00`),
      to: new Date(`${period.to}T23:59:59`),
      userIds: scoped.length > 0 ? scoped : [userId ?? ''],
      ...(projectIds.length > 0 ? { projectIds } : {}),
      searchMethod: 'AND',
    };
  }, [userId, period.from, period.to, scope.resolvedUserIds, scope.roster, projectIds]);

  const runReport = useCallback(
    async (input: WorkSessionsReportInput) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await ReportsApi.getWorkSessionsPerUser(input);
        if (seq !== requestSeq.current) return;
        if (result.errors.length > 0) {
          setError(getApiErrorMessage(result, t, t('reports.errorTitle')));
          return;
        }
        setRows(result.payload ?? []);
        setHasRun(true);
      } catch (err) {
        if (seq === requestSeq.current) setError(getApiErrorMessage(err, t, t('reports.errorTitle')));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [t],
  );

  // The roster is the "All employees" list this endpoint needs NAMED (see buildInput). Until the
  // scope hook has loaded it, a run would fall back to the caller alone and answer "Nothing to
  // report" for a whole company. Defer instead.
  const [runWhenScopeReady, setRunWhenScopeReady] = useState(false);

  const search = useCallback(async () => {
    if (invalidRange) return;
    if (scope.loading && scope.resolvedUserIds.length === 0) {
      setRunWhenScopeReady(true);
      setLoading(true);
      setError(null);
      return;
    }
    // A scope the user set that matches nobody must NOT reach the server: it would arrive as an
    // empty list, which this endpoint reads as "everyone", i.e. it would answer a filter naming
    // nobody with the whole company.
    if (scope.matchedNobody) {
      requestSeq.current += 1;
      setRows([]);
      setError(null);
      setHasRun(true);
      return;
    }
    await runReport(buildInput());
  }, [invalidRange, scope.loading, scope.resolvedUserIds.length, scope.matchedNobody, runReport, buildInput]);

  useEffect(() => {
    if (runWhenScopeReady && !scope.loading) {
      setRunWhenScopeReady(false);
      void search();
    }
  }, [runWhenScopeReady, scope.loading, search]);

  /* ── grouping ─────────────────────────────────────────────────────────────────────────────── */

  const rowKeyOf = useCallback((row: WorkSessionReportView) => String(row.id), []);

  const departmentName = useCallback(
    (employeeId: string) => {
      const person = scope.roster.find((p) => p.id === employeeId);
      if (!person?.departmentId) return '';
      return scope.departments.find((d) => d.id === person.departmentId)?.name ?? '';
    },
    [scope.roster, scope.departments],
  );

  const groups = useMemo<WorkSessionsGroup[]>(() => {
    const byUser = new Map<string, { rows: WorkSessionReportView[]; totals: WorkSessionsTotals }>();
    for (const row of rows) {
      const id = row.userId ?? '';
      const bucket = byUser.get(id) ?? { rows: [], totals: emptyTotals() };
      bucket.rows.push(row);
      bucket.totals = addRow(bucket.totals, row);
      byUser.set(id, bucket);
    }

    return Array.from(byUser.entries()).map(([employeeId, bucket]) => {
      const first = bucket.rows[0];
      // Ascending by start, so a group reads as a diary.
      const ordered = [...bucket.rows].sort(
        (a, b) => String(a.startTime).localeCompare(String(b.startTime)),
      );
      const department = departmentName(employeeId);
      const projectCount = bucket.totals.projectIds.size;
      const subtitle = [
        department,
        projectCount > 0 ? t('reports.workSessions.projectCount', { value: projectCount }) : '',
      ]
        .filter(Boolean)
        .join(' · ');

      return {
        key: employeeId || rowKeyOf(first),
        employeeId,
        totals: bucket.totals,
        title: first?.user?.fullName ?? '',
        subtitle: subtitle || undefined,
        badges: (
          <>
            {/* An unfinished session is the only thing here that makes the group's own hours a
                MOVING figure rather than a fact, so it is said on the band, not only in a cell. */}
            {bucket.totals.open > 0 && (
              <StatusChip tone="warning" icon={Hourglass} data-testid="ws-group-open">
                {t('reports.workSessions.openBadge', { value: bucket.totals.open })}
              </StatusChip>
            )}
          </>
        ),
        rows: ordered,
      };
    });
  }, [rows, departmentName, rowKeyOf, t]);

  const totals = useMemo(() => {
    const acc = emptyTotals();
    let sessions = 0;
    let hours = 0;
    let open = 0;
    for (const group of groups) {
      sessions += group.totals.sessions;
      hours += group.totals.hours;
      open += group.totals.open;
      for (const id of group.totals.projectIds) acc.projectIds.add(id);
    }
    return { sessions, hours, open, projectIds: acc.projectIds };
  }, [groups]);

  const collapse = useGroupCollapse(groups, false);

  /* ── cells ────────────────────────────────────────────────────────────────────────────────── */

  const muted = (text: string = EMPTY_VALUE) => (
    <span className="text-muted-foreground/60">{text}</span>
  );

  const timeCell = (value: string | Date | null | undefined) => {
    const text = wallClock(value);
    return text ? <span className="tabular-nums">{text}</span> : muted();
  };

  /**
   * The END cell carries the whole difference between a finished session and a running one. A bare
   * `—` would read as missing data; the marker says the session is still going, which is also why
   * its duration keeps moving.
   */
  const renderEnd = (row: WorkSessionReportView) => {
    if (isOpen(row)) {
      return (
        <StatusChip tone="warning" icon={Hourglass} data-testid={`ws-open-${row.id}`}>
          {t('reports.workSessions.openSession')}
        </StatusChip>
      );
    }
    return timeCell(row.endTime);
  };

  const truncated = (value: string | null | undefined) =>
    value ? (
      <span className="block max-w-[18rem] truncate" title={value}>
        {value}
      </span>
    ) : (
      muted()
    );

  /* ── columns ──────────────────────────────────────────────────────────────────────────────────
   *
   * 🔴 THE `key` OF EACH COLUMN IS THE EXPORT CONTRACT. `reports/Export/WorkSessions` writes its row
   * dictionary under exactly these names (date, project, start, end, duration, startComment,
   * endComment), so renaming one here silently blanks a column in the file.
   *
   * The two comment columns are deliberately SEPARATE. The old tab printed
   * `Start Task : x | End Task : y` into one cell with the labels inside the VALUE, which meant a
   * file could not address either half and a reader could not sort or scan them.
   */
  const columns = useMemo<ReportColumn<WorkSessionReportView>[]>(
    () => [
      {
        key: 'date',
        label: t('reports.workSessions.date'),
        kind: 'date',
        width: '11rem',
        emphasis: true,
        value: (r) => dayKeyOf(r.startTime),
        render: (r) => {
          const day = dayKeyOf(r.startTime);
          return day ? formatDayShort(day, lang) : muted();
        },
      },
      {
        key: 'project',
        label: t('reports.workSessions.project'),
        kind: 'text',
        align: 'start',
        // A session with no project is the ordinary case for a company that does not use projects —
        // it is not an error and must not print a raw id.
        value: (r) => r.project?.title ?? '',
        render: (r) =>
          r.project?.title
            ? <span className="truncate" title={r.project.title}>{r.project.title}</span>
            : muted(t('reports.workSessions.noProject')),
      },
      {
        key: 'start',
        label: t('reports.workSessions.start'),
        kind: 'text',
        align: 'start',
        width: '6rem',
        value: (r) => wallClock(r.startTime) ?? '',
        render: (r) => timeCell(r.startTime),
      },
      {
        key: 'end',
        label: t('reports.workSessions.end'),
        kind: 'text',
        align: 'start',
        value: (r) => wallClock(r.endTime) ?? '',
        render: renderEnd,
      },
      {
        key: 'duration',
        label: t('reports.workSessions.duration'),
        kind: 'hours',
        total: 'sum',
        tooltip: t('reports.workSessions.durationHint'),
        value: (r) => hoursOf(r),
        render: (r) => {
          const hours = hoursOf(r);
          return Math.abs(hours) >= HOUR_EPSILON
            ? <span className="font-medium">{formatHours(hours)}</span>
            : muted();
        },
      },
      {
        key: 'startComment',
        label: t('reports.workSessions.startComment'),
        kind: 'text',
        align: 'start',
        muted: true,
        hideBelow: 'lg',
        value: (r) => r.comments?.[0] ?? '',
        render: (r) => truncated(r.comments?.[0]),
      },
      {
        key: 'endComment',
        label: t('reports.workSessions.endComment'),
        kind: 'text',
        align: 'start',
        muted: true,
        hideBelow: 'lg',
        value: (r) => r.comments?.[1] ?? '',
        render: (r) => truncated(r.comments?.[1]),
      },
    ],
    // The render closures are re-created every render, so listing them would defeat the memo. What
    // they READ is listed instead — the language (dates, chips, labels).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, lang],
  );

  /* ── summary strip ────────────────────────────────────────────────────────────────────────── */

  const summaryItems = useMemo<ReportSummaryItem[]>(() => {
    if (rows.length === 0) return [];
    return [
      {
        label: t('reports.workSessions.employees'),
        value: formatInt(groups.length, lang),
        icon: Users,
        testId: 'ws-employees',
      },
      {
        label: t('reports.workSessions.count'),
        value: formatInt(totals.sessions, lang),
        icon: ListChecks,
        testId: 'ws-count',
      },
      {
        label: t('reports.workSessions.duration'),
        value: formatHours(totals.hours),
        hint: totals.open > 0 ? t('reports.workSessions.durationOpenHint') : undefined,
        icon: Timer,
        testId: 'ws-duration-total',
      },
      {
        // The one figure on this strip that is an ACTION: a session nobody stopped is a row somebody
        // has to close before the period can be trusted.
        label: t('reports.workSessions.openCount'),
        value: formatInt(totals.open, lang),
        hint: totals.open > 0 ? t('reports.workSessions.openHint') : undefined,
        tone: totals.open > 0 ? 'warning' : 'default',
        icon: Hourglass,
        testId: 'ws-open',
      },
      {
        label: t('reports.workSessions.projectsCount'),
        value: formatInt(totals.projectIds.size, lang),
        icon: FolderKanban,
        testId: 'ws-projects-count',
      },
    ];
  }, [rows.length, groups.length, totals, lang, t]);

  /* ── export ───────────────────────────────────────────────────────────────────────────────── */

  const periodText = formatPeriod(period.from, period.to, lang);
  const scopeText = scope.scopeText;

  /**
   * The client does NOT render the file. It posts the SAME filter the screen ran plus a description
   * of the columns, and the server re-runs the query and renders the document — so the file and the
   * screen cannot disagree about a figure, and all three formats come from one pipeline instead of
   * the two the legacy `reports/WorkSessions/{format}` route offered. That route is still live for
   * mobile; this tab no longer calls it.
   */
  const onExport = (format: ReportExportFormat) => {
    const title = t('reports.workSessions.title');
    const request = buildExportRequest<WorkSessionsReportInput, WorkSessionReportView>({
      input: buildInput(),
      format,
      title,
      columns,
      culture: lang,
      rtl: i18n.dir() === 'rtl',
      labels: { ...reportExportLabels(t), period: periodText, scope: scopeText },
    });
    void exportReport(
      format,
      () => postReportExport('reports/Export/WorkSessions', request),
      reportExportFilename(title, periodText, format),
    );
  };

  /* ── render ───────────────────────────────────────────────────────────────────────────────── */

  const projectChipLabel = useMemo(() => {
    if (projectIds.length === 0) return '';
    if (projectIds.length <= 3) {
      return projectIds
        .map((id) => projects.find((p) => p.id === id)?.title ?? String(id))
        .join(', ');
    }
    return t('reports.workSessions.projectCount', { value: projectIds.length });
  }, [projectIds, projects, t]);

  const filterChips = hasRun
    ? [
        { key: 'period', label: periodText, tone: 'accent' as const },
        { key: 'scope', label: scopeText },
        ...(projectChipLabel ? [{ key: 'projects', label: projectChipLabel }] : []),
      ]
    : [];

  return (
    <PageLayout
      title={t('reports.workSessions.title')}
      description={t('reports.workSessions.subtitle')}
      icon={Briefcase}
      accentColor="purple"
      maxWidth="1600px"
    >
      <div className="space-y-6">
        <ReportFilterBar
          testId="ws-filters"
          chips={filterChips}
          actions={
            <>
              <RunButton
                testId="generate-report"
                loading={loading}
                disabled={loading || invalidRange}
                onClick={() => void search()}
              />
              <ExportMenu
                onExport={onExport}
                busy={exporting}
                disabled={!hasRun || rows.length === 0}
              />
            </>
          }
        >
          <ReportFilterField label={t('reports.workSessions.dateRange')}>
            <PeriodPicker mode="day" value={period} onChange={setPeriod} disabled={loading} />
          </ReportFilterField>

          <ReportFilterField label={t('reports.workSessions.projects')} className="min-w-[14rem]">
            <Select
              multiple
              searchable
              clearable
              disabled={loading || projects.length === 0}
              options={projects.map((p) => ({ value: String(p.id), label: p.title }))}
              value={projectIds.map(String)}
              placeholder={t('reports.workSessions.allProjects')}
              onChange={(next: unknown) =>
                setProjectIds(
                  (Array.isArray(next) ? next : [])
                    .map((v) => Number(v))
                    .filter((n) => Number.isFinite(n)),
                )
              }
              data-testid="ws-projects"
            />
          </ReportFilterField>

          <ReportFilterField label={t('reports.scope')} className="max-w-4xl">
            <ScopeFilter scope={scope} disabled={loading} testId="scope-filter" />
          </ReportFilterField>
        </ReportFilterBar>

        {loading ? (
          <ReportLoading columns={columns.length} />
        ) : error ? (
          <ReportError message={error} onRetry={() => void search()} />
        ) : !hasRun ? (
          <ReportIdle hint={t('reports.workSessions.idleHint')} />
        ) : (
          <div className="space-y-4">
            <ReportSummaryStrip
              items={summaryItems}
              testId="summary-ws"
              ariaLabel={t('reports.workSessions.title')}
            />

            {/* Said once, so nobody has to work out why a total moved between two loads. */}
            {totals.open > 0 && (
              <p className="max-w-[80ch] text-xs text-muted-foreground">
                {t('reports.workSessions.openExplainer')}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => (collapse.allCollapsed ? collapse.expandAll() : collapse.collapseAll())}
                data-testid="ws-collapse-toggle"
                disabled={groups.length === 0}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground-strong transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                {collapse.allCollapsed ? t('reports.expandAll') : t('reports.collapseAll')}
              </button>
            </div>

            <ReportTable
              columns={columns}
              groups={groups}
              collapse={collapse}
              rowKey={rowKeyOf}
              rowTestId={(r) => `ws-row-${r.id}`}
              density="compact"
              emptyState={<ReportEmpty period={periodText} scopeText={scopeText} />}
              testId="ws-table"
              ariaLabel={t('reports.workSessions.title')}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
}
